import type { Json, Tables } from '@hive/shared';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { queryKeys } from './keys';

// Comms' data: channels, messages and threads, reactions, attachments, and the
// member's own read state and notification settings per channel.

export type Channel = Tables<'channels'>;
export type Message = Omit<Tables<'messages'>, 'search'>;
export type Reaction = Pick<Tables<'message_reactions'>, 'id' | 'message_id' | 'member_id' | 'emoji' | 'import_meta'>;
export type ChannelSettings = Pick<Tables<'channel_members'>, 'channel_id' | 'last_read_at' | 'notify' | 'starred'>;
export type NotifyLevel = 'all' | 'mentions' | 'none';

const MESSAGE_COLUMNS =
  'id, channel_id, parent_id, author_id, author_kind, on_behalf_of, body, link_preview, edited_at, deleted_at, reply_count, last_reply_at, source, source_id, import_meta, created_at, updated_at, created_by';

/** How many top-level messages a page of history holds. */
export const PAGE_SIZE = 50;

export function useChannels() {
  return useQuery({
    queryKey: queryKeys.channels,
    queryFn: async (): Promise<Channel[]> => {
      const { data, error } = await supabase().from('channels').select('*').order('name');
      if (error) throw error;
      return data;
    },
  });
}

export function useCreateChannel() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (channel: { name: string; topic: string; game_id: string | null }) => {
      const { data, error } = await supabase().from('channels').insert(channel).select('*').single();
      if (error) {
        if (error.code === '23505') throw new Error(`There's already a #${channel.name}.`);
        if (error.code === '23514') throw new Error('Channel names use a–z, 0–9, - and _.');
        throw error;
      }
      return data;
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: queryKeys.channels }),
  });
}

export function useUpdateChannel() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      patch,
    }: {
      id: string;
      patch: Partial<Pick<Channel, 'name' | 'topic' | 'game_id' | 'archived_at'>>;
    }) => {
      const { error } = await supabase().from('channels').update(patch).eq('id', id);
      if (error) {
        if (error.code === '23505') throw new Error(`There's already a #${patch.name}.`);
        throw error;
      }
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: queryKeys.channels }),
  });
}

/** A channel's top-level messages, newest page first; older pages load on demand. */
export function useChannelMessages(channelId: string | undefined) {
  return useInfiniteQuery({
    queryKey: queryKeys.channelMessages(channelId ?? ''),
    enabled: !!channelId,
    initialPageParam: null as string | null,
    queryFn: async ({ pageParam }): Promise<Message[]> => {
      let q = supabase()
        .from('messages')
        .select(MESSAGE_COLUMNS)
        .eq('channel_id', channelId!)
        .is('parent_id', null)
        .order('created_at', { ascending: false })
        .limit(PAGE_SIZE);
      if (pageParam) q = q.lt('created_at', pageParam);
      const { data, error } = await q;
      if (error) throw error;
      return data as Message[];
    },
    getNextPageParam: (last) => (last.length === PAGE_SIZE ? last.at(-1)!.created_at : null),
  });
}

/** A thread: its root and replies, oldest first. */
export function useThread(rootId: string | null) {
  return useQuery({
    queryKey: queryKeys.thread(rootId ?? ''),
    enabled: !!rootId,
    queryFn: async (): Promise<Message[]> => {
      const { data, error } = await supabase()
        .from('messages')
        .select(MESSAGE_COLUMNS)
        .or(`id.eq.${rootId},parent_id.eq.${rootId}`)
        .order('created_at');
      if (error) throw error;
      return data as Message[];
    },
  });
}

/** One message, for opening a link to it. */
export function useMessage(id: string | null) {
  return useQuery({
    queryKey: ['messages', 'one', id],
    enabled: !!id,
    queryFn: async (): Promise<Message | null> => {
      const { data, error } = await supabase().from('messages').select(MESSAGE_COLUMNS).eq('id', id!).maybeSingle();
      if (error) throw error;
      return data as Message | null;
    },
  });
}

export interface NewMessage {
  channel_id: string;
  parent_id?: string | null;
  body: string;
  link_preview?: Json | null;
}

/** Posts a message, then attaches what was uploaded for it. */
export async function postMessage(
  message: NewMessage,
  attachments: readonly { type: 'file' | 'vault_link'; id: string }[] = [],
): Promise<Message> {
  const client = supabase();
  const { data, error } = await client.from('messages').insert(message).select(MESSAGE_COLUMNS).single();
  if (error) throw error;
  if (attachments.length) {
    const { error: linkError } = await client.from('links').insert(
      attachments.map((a) => ({
        from_type: 'message',
        from_id: data.id,
        to_type: a.type,
        to_id: a.id,
        kind: 'attached',
      })),
    );
    if (linkError) throw linkError;
  }
  return data as Message;
}

export function usePostMessage() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      message,
      attachments,
    }: {
      message: NewMessage;
      attachments?: readonly { type: 'file' | 'vault_link'; id: string }[];
    }) => postMessage(message, attachments),
    onSettled: (_d, _e, { message }) =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.channelMessages(message.channel_id) }),
        message.parent_id ? queryClient.invalidateQueries({ queryKey: queryKeys.thread(message.parent_id) }) : null,
        queryClient.invalidateQueries({ queryKey: queryKeys.attachments }),
      ]),
  });
}

export function useEditMessage() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, patch }: { id: string; patch: { body?: string; deleted_at?: string } }) => {
      const { data, error } = await supabase().from('messages').update(patch).eq('id', id).select('id');
      if (error) throw error;
      if (!data.length) throw new Error('Only the author can change a message.');
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: queryKeys.messages }),
  });
}

/** Reactions on a channel's messages (threads included). */
export function useReactions(channelId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.reactions(channelId ?? ''),
    enabled: !!channelId,
    queryFn: async (): Promise<Reaction[]> => {
      const { data, error } = await supabase()
        .from('message_reactions')
        .select('id, message_id, member_id, emoji, import_meta, messages!inner(channel_id)')
        .eq('messages.channel_id', channelId!);
      if (error) throw error;
      return data.map(({ messages: _m, ...r }) => r);
    },
  });
}

export function useToggleReaction() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      messageId,
      emoji,
      mine,
    }: {
      messageId: string;
      emoji: string;
      mine: Reaction | undefined;
    }) => {
      const client = supabase();
      const { error } = mine
        ? await client.from('message_reactions').delete().eq('id', mine.id)
        : await client.from('message_reactions').insert({ message_id: messageId, emoji });
      if (error && error.code !== '23505') throw error;
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: queryKeys.allReactions }),
  });
}

/** What's attached to these messages: links from each message of kind attached. */
export function useAttachments(messageIds: readonly string[]) {
  const ids = [...messageIds].sort();
  return useQuery({
    queryKey: [...queryKeys.attachments, ids],
    enabled: ids.length > 0,
    queryFn: async () => {
      const { data, error } = await supabase()
        .from('links')
        .select('from_id, to_type, to_id')
        .eq('from_type', 'message')
        .eq('kind', 'attached')
        .in('from_id', ids);
      if (error) throw error;
      return data;
    },
  });
}

/** The signed-in member's settings for every channel they've opened. */
export function useChannelSettings() {
  return useQuery({
    queryKey: queryKeys.channelSettings,
    queryFn: async (): Promise<ChannelSettings[]> => {
      const { data, error } = await supabase()
        .from('channel_members')
        .select('channel_id, last_read_at, notify, starred');
      if (error) throw error;
      return data;
    },
  });
}

export function useUpdateChannelSettings() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      channelId,
      patch,
    }: {
      channelId: string;
      patch: Partial<Pick<ChannelSettings, 'notify' | 'starred'>>;
    }) => {
      const client = supabase();
      // A row exists once the channel has been opened; make one if not.
      const { data, error } = await client
        .from('channel_members')
        .update(patch)
        .eq('channel_id', channelId)
        .select('id');
      if (error) throw error;
      if (data.length === 0) {
        const { error: insertError } = await client.from('channel_members').insert({ channel_id: channelId, ...patch });
        if (insertError) throw insertError;
      }
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: queryKeys.channelSettings }),
  });
}

export interface UnreadRow {
  channel_id: string;
  unread: number;
  mentions: number;
  last_message_at: string | null;
}

export function useUnread() {
  return useQuery({
    queryKey: queryKeys.unread,
    queryFn: async (): Promise<UnreadRow[]> => {
      const { data, error } = await supabase().rpc('channel_unread');
      if (error) throw error;
      return data;
    },
  });
}

export async function markChannelRead(channelId: string): Promise<void> {
  const { error } = await supabase().rpc('mark_channel_read', { p_channel_id: channelId });
  if (error) throw error;
}

/** How much a channel holds, for the delete warning. */
export function useChannelStats(channelId: string, enabled = true) {
  return useQuery({
    queryKey: [...queryKeys.channelMessages(channelId), 'stats'],
    enabled,
    queryFn: async () => {
      const client = supabase();
      const count = async (replies: boolean) => {
        let q = client.from('messages').select('id', { count: 'exact', head: true }).eq('channel_id', channelId);
        q = replies ? q.not('parent_id', 'is', null) : q.is('parent_id', null);
        const { count: n, error } = await q;
        if (error) throw error;
        return n ?? 0;
      };
      const [messages, replies] = await Promise.all([count(false), count(true)]);
      return { messages, replies };
    },
  });
}

/** Deletes a channel and everything in it (the database refuses #general). */
export function useDeleteChannel() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { data, error } = await supabase().from('channels').delete().eq('id', id).select('id');
      if (error) throw error;
      if (!data.length) throw new Error('That channel was already gone, or you can’t delete it.');
    },
    onSettled: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.channels }),
        queryClient.invalidateQueries({ queryKey: queryKeys.messages }),
        queryClient.invalidateQueries({ queryKey: queryKeys.unread }),
        queryClient.invalidateQueries({ queryKey: queryKeys.links }),
      ]),
  });
}
